// Languages the storefront is published in. English is the canonical language: every
// record is written in English first, and other languages are stored as translations
// that fall back to English field by field.
export const LOCALES = ['en', 'lt'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';
/** Languages stored as translations (everything except the default). */
export const TRANSLATED_LOCALES = ['lt'] as const;
export type TranslatedLocale = (typeof TRANSLATED_LOCALES)[number];

export const LOCALE_TAGS: Record<Locale, string> = { en: 'en-GB', lt: 'lt-LT' };
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', lt: 'Lietuvių' };

export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
export const toLocale = (v: unknown): Locale => (isLocale(v) ? v : DEFAULT_LOCALE);

/** "/shop" -> "/lt/shop" for Lithuanian; English URLs have no prefix. External links are left alone. */
export function localizePath(path: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE || !path.startsWith('/') || path.startsWith('//')) return path;
  if (path === `/${locale}` || path.startsWith(`/${locale}/`) || path.startsWith(`/${locale}?`)) return path;
  // "/" and "/?q=…" become "/lt" and "/lt?q=…", without a trailing slash.
  return path === '/' || path.startsWith('/?') || path.startsWith('/#') ? `/${locale}${path.slice(1)}` : `/${locale}${path}`;
}

/** Splits "/lt/shop?x=1" into { locale: 'lt', path: '/shop?x=1' }. */
export function splitLocalePath(path: string): { locale: Locale; path: string } {
  for (const l of TRANSLATED_LOCALES) {
    if (path === `/${l}`) return { locale: l, path: '/' };
    if (path.startsWith(`/${l}/`) || path.startsWith(`/${l}?`)) {
      const rest = path.slice(l.length + 1);
      return { locale: l, path: rest.startsWith('?') ? `/${rest}` : rest };
    }
  }
  return { locale: DEFAULT_LOCALE, path };
}

// ---------------------------------------------------------------------------
// Second-hand item details
// ---------------------------------------------------------------------------
export const CONDITIONS = ['excellent', 'very_good', 'good', 'fair'] as const;
export type Condition = (typeof CONDITIONS)[number];

export const CONDITION_LABELS: Record<Locale, Record<Condition, string>> = {
  en: { excellent: 'Excellent', very_good: 'Very good', good: 'Good', fair: 'Well loved' },
  lt: { excellent: 'Puiki', very_good: 'Labai gera', good: 'Gera', fair: 'Su naudojimo žymėmis' },
};

export const CONDITION_HINTS: Record<Locale, Record<Condition, string>> = {
  en: {
    excellent: 'Barely used, no marks worth mentioning.',
    very_good: 'Light signs of use that you have to look for.',
    good: 'Honest wear from years of use, fully working.',
    fair: 'Visible wear, chips or fading — priced to match.',
  },
  lt: {
    excellent: 'Beveik nenaudotas, be žymių.',
    very_good: 'Lengvi naudojimo ženklai, kuriuos reikia paieškoti.',
    good: 'Natūralus nusidėvėjimas po metų naudojimo, viskas veikia.',
    fair: 'Matomas nusidėvėjimas, nuskilimai ar išblukimas — kaina atitinkama.',
  },
};

export const DECADES = [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010] as const;

/** 1970 -> "1970s" / "1970-ieji" */
export function decadeLabel(decade: number, locale: Locale): string {
  return locale === 'lt' ? `${decade}-ieji` : `${decade}s`;
}
